import { Link, useParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { useStartProgress } from '@/app/progress';
import { GLOSSARY_TERMS, GUIDE_PAGES, START_STEPS } from '@/routes/guide/pages';
import { Definition } from '@/ui/Explain';
import { PageHeader, Panel } from '@/ui/Panel';

/**
 * The manual, and the path through it.
 *
 * "Start here" is not a static list: it reads what the coach has actually done
 * and ticks the steps off. A checklist that knows where you are is the
 * difference between documentation and onboarding — and it is the page a coach
 * opening the product for the first time needs before any other.
 */
export function Guide({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { page } = useParams<{ page?: string }>();
  const current = GUIDE_PAGES.find((p) => p.key === page) ?? GUIDE_PAGES[0]!;
  const index = GUIDE_PAGES.indexOf(current);

  return (
    <>
      <PageHeader title={t('guide.title')} lead={t('guide.lead')} />

      <div className="grid gap-5 lg:grid-cols-[16rem_1fr] lg:items-start">
        <nav aria-label={t('guide.contents')} className="lg:sticky lg:top-4">
          <Panel title={t('guide.contents')}>
            <ol className="divide-y divide-line">
              {GUIDE_PAGES.map((p, i) => (
                <li key={p.key}>
                  <Link
                    to={localePath(locale, p.key === 'start' ? '/guide' : `/guide/${p.key}`)}
                    aria-current={p.key === current.key ? 'page' : undefined}
                    className={`flex items-baseline gap-2 px-4 py-2.5 transition-colors hover:bg-surface-sunken ${
                      p.key === current.key ? 'bg-signal-soft font-medium' : 'text-ink-muted'
                    }`}
                  >
                    <span className="tabular w-4 shrink-0 text-meta text-ink-faint">{i + 1}</span>
                    <span className="min-w-0">{t(`guide.pages.${p.key}.title`)}</span>
                  </Link>
                </li>
              ))}
            </ol>
          </Panel>
        </nav>

        <div className="grid gap-5">
          {current.special === 'start' ? (
            <StartHere locale={locale} />
          ) : current.special === 'glossary' ? (
            <Glossary />
          ) : (
            <Panel title={t(`guide.pages.${current.key}.title`)}>
              <div className="grid gap-5 p-4">
                <p className="max-w-[70ch] text-ink-muted">
                  {t(`guide.pages.${current.key}.lead`)}
                </p>
                {current.sections.map((section) => (
                  <section key={section.heading}>
                    <h2 className="font-condensed text-subhead font-semibold tracking-tight">
                      {t(`guide.pages.${current.key}.${section.heading}`)}
                    </h2>
                    <p className="mt-1 max-w-[70ch] leading-relaxed">
                      {t(`guide.pages.${current.key}.${section.body}`)}
                    </p>
                    {section.where ? (
                      <p className="mt-1 text-meta text-ink-faint">
                        {t(`guide.pages.${current.key}.${section.where}`)}
                      </p>
                    ) : null}
                  </section>
                ))}
              </div>
            </Panel>
          )}

          <nav className="flex flex-wrap items-center justify-between gap-3">
            {index > 0 ? (
              <Link
                to={localePath(
                  locale,
                  GUIDE_PAGES[index - 1]!.key === 'start'
                    ? '/guide'
                    : `/guide/${GUIDE_PAGES[index - 1]!.key}`,
                )}
                className="inline-flex min-h-11 items-center text-meta text-ink-muted underline-offset-2 hover:text-ink hover:underline"
              >
                ← {t(`guide.pages.${GUIDE_PAGES[index - 1]!.key}.title`)}
              </Link>
            ) : (
              <span />
            )}
            {index < GUIDE_PAGES.length - 1 ? (
              <Link
                to={localePath(locale, `/guide/${GUIDE_PAGES[index + 1]!.key}`)}
                className="inline-flex min-h-11 items-center text-meta text-ink-muted underline-offset-2 hover:text-ink hover:underline"
              >
                {t(`guide.pages.${GUIDE_PAGES[index + 1]!.key}.title`)} →
              </Link>
            ) : (
              <span />
            )}
          </nav>
        </div>
      </div>
    </>
  );
}

/**
 * The eight steps, ticked off from what the coach has actually done.
 *
 * The checks live in `useStartProgress` because the dashboard shows the same
 * progress and two copies would disagree the first time either changed.
 */
function StartHere({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { state, done } = useStartProgress();

  return (
    <Panel
      title={t('guide.pages.start.title')}
      count={t('guide.progress', { done, total: START_STEPS.length })}
    >
      <div className="grid gap-2 p-4">
        <p className="max-w-[70ch] text-ink-muted">{t('guide.pages.start.lead')}</p>
        <p className="max-w-[70ch] text-meta text-ink-muted">{t('guide.pages.start.intro')}</p>
      </div>

      <ol className="divide-y divide-line border-t border-line">
        {START_STEPS.map((step, i) => {
          const complete = state[step.key];
          return (
            <li key={step.key} className="flex items-start gap-3 px-4 py-3">
              <span
                aria-hidden="true"
                className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border text-meta font-semibold ${
                  complete
                    ? 'border-ok bg-ok text-surface'
                    : 'border-line text-ink-faint'
                }`}
              >
                {complete ? '✓' : i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-3">
                  <Link
                    to={localePath(locale, step.to)}
                    className="inline-flex min-h-11 items-center font-medium underline-offset-2 hover:underline"
                  >
                    {t(`guide.pages.start.steps.${step.key}.title`)}
                  </Link>
                  <span className="text-meta text-ink-faint">
                    {t(`guide.pages.start.steps.${step.key}.where`)}
                  </span>
                  <span className={`ms-auto text-meta ${complete ? 'text-ok' : 'text-ink-faint'}`}>
                    {complete ? t('guide.done') : t('guide.todo')}
                  </span>
                </span>
                <span className="mt-1 block max-w-[70ch] leading-relaxed">
                  {t(`guide.pages.start.steps.${step.key}.body`)}
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      <div className="border-t border-line p-4">
        <h2 className="font-condensed text-subhead font-semibold tracking-tight">
          {t('guide.pages.start.after')}
        </h2>
        <p className="mt-1 max-w-[70ch] leading-relaxed">{t('guide.pages.start.afterBody')}</p>
      </div>
    </Panel>
  );
}

/** Every metric the app shows, with the four answers that keep it honest. */
function Glossary() {
  const t = useTranslations();
  return (
    <Panel title={t('guide.pages.numbers.title')} count={GLOSSARY_TERMS.length}>
      <div className="grid gap-2 p-4">
        <p className="max-w-[70ch] text-ink-muted">{t('guide.pages.numbers.lead')}</p>
        <p className="max-w-[70ch] text-meta text-ink-muted">{t('guide.pages.numbers.intro')}</p>
      </div>
      <ul className="divide-y divide-line border-t border-line">
        {GLOSSARY_TERMS.map((term) => (
          <li key={term} className="px-4 py-4">
            <h2 className="font-condensed text-subhead font-semibold tracking-tight">
              {t(`knowledge.term.${term}.label`)}
            </h2>
            <div className="mt-2 max-w-[70ch]">
              <Definition base={`knowledge.term.${term}`} />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
